// hooks/useVideoControls.ts
import { useCallback, useRef } from "react";
import { SLIDER_PRECISION, VideoPlayerState } from "./useVideoPlayerState";

interface UseVideoControlsProps {
  playerState: VideoPlayerState;
}

export const useVideoControls = ({
  playerState
}: UseVideoControlsProps) => {
  const {
    duration,
    currentTime,
    isPlaying,
    setIsPlaying,
    setCurrentTime,
    setSliderValue,
    getVideos,
    getPrimaryVideo,
  } = playerState;

  const pendingSeekTimeRef = useRef<number | null>(null);
  const seekRafRef = useRef<number | null>(null);

  const seekAllVideos = useCallback((newTime: number) => {
    const safeTime = Math.max(0, newTime);
    getVideos().forEach((v) => {
      if (!v) return;
      try {
        if (typeof (v as any).fastSeek === "function") {
          (v as any).fastSeek(safeTime);
        } else {
          v.currentTime = safeTime;
        }
      } catch {
        v.currentTime = safeTime;
      }
    });
  }, [getVideos]);

  const flushPendingSeek = useCallback(() => {
    const pending = pendingSeekTimeRef.current;
    seekRafRef.current = null;
    if (pending == null) return;
    seekAllVideos(pending);
    setCurrentTime(pending);
  }, [seekAllVideos, setCurrentTime]);

  // Handler for video's timeupdate event
  const handleVideoTimeUpdate = useCallback(() => {
    const primary = getPrimaryVideo();
    if (!primary || duration === 0) return;

    const time = primary.currentTime;
    setCurrentTime(time);
    setSliderValue((time / duration) * SLIDER_PRECISION);
  }, [duration, getPrimaryVideo, setCurrentTime, setSliderValue]);

  // Handler for play/pause button (CORRECTED)
  const togglePlay = useCallback(() => {
    setIsPlaying(prevIsPlaying => !prevIsPlaying);
  }, [setIsPlaying]);

  const handleSliderDrag = useCallback((event: unknown, newValue: number | number[]) => {
    if (typeof newValue !== 'number' || duration === 0) return;

    const newTime = (newValue / SLIDER_PRECISION) * duration;
    pendingSeekTimeRef.current = newTime;
    setSliderValue(newValue);
    setCurrentTime(newTime);

    // Limit seeks to once per animation frame and always apply the latest value.
    if (seekRafRef.current == null) {
      seekRafRef.current = window.requestAnimationFrame(flushPendingSeek);
    }
  }, [duration, flushPendingSeek, setCurrentTime, setSliderValue]);


  // RENAMED: This handler now fires only when the user releases the slider.
  const handleSliderChangeCommitted = useCallback((event: unknown, newValue: number | number[]) => {
    if (typeof newValue !== 'number' || duration === 0) return;

    // Pause the video if it was playing.
    if (isPlaying) {
      // It's better to let the state change propagate down.
      setIsPlaying(false);
    }

    // Calculate the final time.
    const newTime = (newValue / SLIDER_PRECISION) * duration;

    if (seekRafRef.current != null) {
      window.cancelAnimationFrame(seekRafRef.current);
      seekRafRef.current = null;
    }
    pendingSeekTimeRef.current = newTime;
    seekAllVideos(newTime);

    // Do the official React state update to sync everything.
    setSliderValue(newValue);
    setCurrentTime(newTime);
  }, [duration, isPlaying, seekAllVideos, setIsPlaying, setSliderValue, setCurrentTime]);

  // Handler for capturing a frame
  const captureFrame = useCallback(() => {
    const primary = getPrimaryVideo();
    if (!primary) return;
    const canvas = document.createElement("canvas");
    canvas.width = primary.videoWidth;
    canvas.height = primary.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(primary, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `frame-${Math.floor(currentTime * 1000)}ms.png`;
    link.click();
  }, [currentTime, getPrimaryVideo]);

  return {
    handleVideoTimeUpdate,
    togglePlay,
    handleSliderDrag,
    handleSliderChangeCommitted,
    captureFrame,
  };
};
